<script lang="ts">
  import { untrack } from 'svelte';
  import Icon from './Icon.svelte';
  import ModalShell from './ModalShell.svelte';
  import { autocompleteCities, addCity, newSessionToken } from './api';
  import type { TripCityView, CityAutocompleteSuggestion } from '~/shared/api.js';

  let {
    tripToken,
    initialQuery = '',
    onAdded,
    onClose,
  }: {
    tripToken: string;
    initialQuery?: string;
    onAdded: (city: TripCityView, activeCityId: number | null) => void;
    onClose: () => void;
  } = $props();

  let query: string = $state(untrack(() => initialQuery));
  let suggestions: CityAutocompleteSuggestion[] = $state([]);
  let saving: boolean = $state(false);
  let error: string | null = $state(null);
  let inputEl: HTMLInputElement | null = $state(null);
  let sessionToken = $state(newSessionToken());

  $effect(() => {
    inputEl?.focus();
  });

  $effect(() => {
    const q = query.trim();
    if (q.length < 2) {
      if (suggestions.length > 0) suggestions = [];
      return;
    }
    const controller = new AbortController();
    const handle = setTimeout(async () => {
      try {
        suggestions = await autocompleteCities(tripToken, q, sessionToken, controller.signal);
        error = null;
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') return;
        error = e instanceof Error ? e.message : 'Search failed';
        suggestions = [];
      }
    }, 250);
    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  });

  async function pick(suggestion: CityAutocompleteSuggestion) {
    if (saving) return;
    saving = true;
    error = null;
    try {
      const result = await addCity(tripToken, suggestion.placeId, sessionToken, true);
      onAdded(result.city, result.activeCityId);
      sessionToken = newSessionToken();
    } catch (e) {
      error = e instanceof Error ? e.message : 'Could not add city';
      saving = false;
    }
  }
</script>

<ModalShell title="Add a city" {onClose} maxWidth={480}>
  <div class="search">
    <Icon name="search" size={16} />
    <input
      bind:this={inputEl}
      type="text"
      bind:value={query}
      placeholder="Search for a city, e.g. Taipei"
      autocomplete="off"
      autocapitalize="off"
      autocorrect="off"
      spellcheck="false"
    />
  </div>

  {#if error}
    <p class="err">{error}</p>
  {/if}

  {#if suggestions.length > 0}
    <ul class="suggestions" role="listbox">
      {#each suggestions as s (s.placeId)}
        <li>
          <button type="button" class="suggestion" disabled={saving} onclick={() => pick(s)}>
            <Icon name="map-pin" size={14} />
            <span class="text">
              <span class="primary">{s.primary}</span>
              <span class="secondary">{s.secondary}</span>
            </span>
          </button>
        </li>
      {/each}
    </ul>
  {:else if query.trim().length >= 2}
    <p class="empty">No cities match "{query.trim()}".</p>
  {:else}
    <p class="empty">Type at least 2 characters to search.</p>
  {/if}
</ModalShell>

<style>
  .search {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    height: 44px;
    padding: 0 0.875rem;
    background: var(--color-canvas);
    border: 1px solid var(--color-hairline-strong);
    border-radius: var(--radius-sm);
    color: var(--color-muted);
  }
  .search:focus-within {
    box-shadow: inset 0 0 0 1px var(--color-ink);
    border-color: var(--color-ink);
    color: var(--color-ink);
  }
  .search input {
    flex: 1;
    background: transparent;
    border: 0;
    outline: none;
    font-family: inherit;
    /* 16px to avoid iOS Safari auto-zoom-on-focus. */
    font-size: 16px;
    color: var(--color-ink);
  }
  .suggestions {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--color-hairline-soft);
    border-radius: var(--radius-md);
    overflow: hidden;
    max-height: 320px;
    overflow-y: auto;
  }
  .suggestion {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    background: transparent;
    border: 0;
    padding: 0.75rem 0.875rem;
    text-align: left;
    cursor: pointer;
    color: var(--color-ink);
    min-height: 52px;
  }
  .suggestion:hover:not(:disabled) { background: var(--color-surface-soft); }
  .suggestion:disabled { opacity: 0.5; cursor: wait; }
  .suggestions li + li .suggestion { border-top: 1px solid var(--color-hairline-soft); }
  .text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
  .primary {
    font-size: 14px;
    font-weight: 500;
    color: var(--color-ink);
  }
  .secondary {
    font-size: 12px;
    color: var(--color-muted);
  }
  .empty {
    margin: 0;
    padding: 0.5rem 0;
    font-size: 13px;
    color: var(--color-muted);
  }
  .err {
    margin: 0;
    font-size: 13px;
    color: var(--color-error);
  }
</style>
